import React from 'react';
import { TransactionApi, PaymentApi, PaymentType, CurrencyObject } from 'common/types';
import {
	Card,
	CardContent,
	Typography,
	IconButton,
	Icon,
	Theme,
	Box,
	Divider,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import moment from 'moment-timezone';
import { getPricingString } from 'common/utils/common';
import Grid from '@mui/material/Grid';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import { PaymentModifyAction } from '../PaymentsList';
import { PaymentWithModifications } from '../PaymentsList/utils';
import ReceiptIcon from '@mui/icons-material/Receipt';
import classNames from 'classnames';
import TransactionRow from './TransactionRow';
import { useTranslation } from 'services/localization/useTranslation';
import { LocaleHelpers } from 'common/locales/helpers';
import { getPaymentAmounts } from 'common/modules/atoms/paymentAmounts';
import { getPaymentStateOrActionTranslation } from 'common/utils/translations';

interface Props {
	paymentWithModifications: PaymentWithModifications;
	handleReceiptPrint: (receiptId: string) => void;
	transactionsLoading: string[];
	modifyTransaction: (transaction: TransactionApi, action: PaymentModifyAction) => void;
	shopCurrency?: CurrencyObject;
}

const PaymentCard = ({
	paymentWithModifications,
	handleReceiptPrint,
	transactionsLoading,
	modifyTransaction,
	shopCurrency,
}: Props) => {
	const classes = useStyles();
	const originalPayment = paymentWithModifications.original.payment;
	const originalTransactions = paymentWithModifications.original.transactions;
	return (
		<Card className={classes.paymentCard} variant="outlined">
			<CardContent className={classes.cardContent}>
				<PaymentCardContent
					originalPayment={true}
					payment={originalPayment}
					transactions={originalTransactions}
					shopCurrency={shopCurrency}
					handleReceiptPrint={handleReceiptPrint}
					transactionsLoading={transactionsLoading}
					modifyTransaction={modifyTransaction}
				/>
				{!!paymentWithModifications.modifications.length && (
					<Divider className={classes.paymentDivider} />
				)}
				{paymentWithModifications.modifications.map(({ payment, transactions }) => {
					return (
						<div key={payment.id} className={classes.modificationPaymentWrapper}>
							<PaymentCardContent
								originalPayment={false}
								payment={payment}
								transactions={transactions}
								shopCurrency={shopCurrency}
								handleReceiptPrint={handleReceiptPrint}
								transactionsLoading={transactionsLoading}
								modifyTransaction={modifyTransaction}
							/>
						</div>
					);
				})}
			</CardContent>
		</Card>
	);
};

interface PaymentCardContentProps {
	originalPayment: boolean;
	payment: PaymentApi;
	transactions: TransactionApi[];
	shopCurrency?: CurrencyObject;
	handleReceiptPrint: (receiptId: string) => void;
	transactionsLoading: string[];
	modifyTransaction: (transaction: TransactionApi, action: PaymentModifyAction) => void;
}

const PaymentCardContent = ({
	originalPayment,
	payment,
	transactions,
	shopCurrency,
	handleReceiptPrint,
	transactionsLoading,
	modifyTransaction,
}: PaymentCardContentProps) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { amount, paymentType } = payment;
	const paymentAmounts = getPaymentAmounts(payment.amount);
	const { value, currency } = amount;
	const { pendingCaptures, pendingRefunds, failedCaptures, failedRefunds } = paymentAmounts;
	const currencyObj = getCurrencyObjectFromCurrencyCode(currency, shopCurrency);
	const singleTransaction = transactions.length === 1;

	const getPaymentTypeString = (paymentType: PaymentType) => {
		return paymentType === 'REFUND'
			? t('common:payment.refund', 'Refund')
			: paymentType === 'DEPOSIT'
			? t('common:payment.deposit', 'Deposit')
			: t('payment.payment', 'Payment');
	};

	const showReceiptForPayment = (transactions: TransactionApi[]) => {
		return transactions.every((t) => t.method !== 'MANUAL' && t.method !== 'POS');
	};

	return (
		<>
			<Grid container justifyContent="space-between" alignItems="center">
				<Grid item>
					<Typography variant="caption">
						{moment(payment.timestamp).format('DD.MM. HH:mm')}
					</Typography>
					{originalPayment && (
						<Typography variant="subtitle2">{getPaymentTypeString(paymentType)}</Typography>
					)}
				</Grid>
				{payment.capture && showReceiptForPayment(transactions) && (
					<Grid item>
						<IconButton
							aria-label="print"
							onClick={() => handleReceiptPrint(payment.receiptId)}
							edge="start"
							size="small"
						>
							<Icon color="inherit">
								<ReceiptIcon />
							</Icon>
						</IconButton>
					</Grid>
				)}
			</Grid>
			<Box pt={1} />
			<Grid container justifyContent="space-between">
				<Grid item>
					<Typography>{getPaymentStateOrActionTranslation(payment.paymentAction, t)}</Typography>
					{singleTransaction && (
						<Typography variant="caption" color="textSecondary" gutterBottom>
							{LocaleHelpers.admin(t).paymentMethods[transactions[0].method] ??
								transactions[0].method}
						</Typography>
					)}
				</Grid>
				<Grid item>
					<Typography>{getPricingString(value, currencyObj)}</Typography>
				</Grid>
			</Grid>
			{!payment.capture && !!paymentAmounts.cancelled ? (
				<Grid container justifyContent="space-between">
					<Grid item>
						<Typography variant="caption" color="textSecondary">
							{t('common:payment.cancelled')}
						</Typography>
					</Grid>
					<Grid item>
						<Typography variant="caption" color="textSecondary">
							{getPricingString(paymentAmounts.cancelled, currencyObj)}
						</Typography>
					</Grid>
				</Grid>
			) : null}
			{!!pendingRefunds ? (
				<Grid container justifyContent="space-between">
					<Grid item>
						<Typography variant="caption" color="textSecondary">
							{t('common:payment.pendingRefunds', 'Pending refunds')}
						</Typography>
					</Grid>
					<Grid item>
						<Typography variant="caption" color="textSecondary">
							{getPricingString(pendingRefunds, currencyObj)}
						</Typography>
					</Grid>
				</Grid>
			) : null}
			{!!failedRefunds ? (
				<Grid container justifyContent="space-between">
					<Grid item>
						<Typography variant="caption" color="error">
							{t('common:payment.failedRefunds', 'Failed refunds')}
						</Typography>
					</Grid>
					<Grid item>
						<Typography variant="caption" color="error">
							{getPricingString(failedRefunds, currencyObj)}
						</Typography>
					</Grid>
				</Grid>
			) : null}
			{!!pendingCaptures ? (
				<Grid container justifyContent="space-between">
					<Grid item>
						<Typography variant="caption" color="textSecondary">
							{t('common:payment.pendingCaptures', 'Pending captures')}
						</Typography>
					</Grid>
					<Grid item>
						<Typography variant="caption" color="textSecondary">
							{getPricingString(pendingCaptures, currencyObj)}
						</Typography>
					</Grid>
				</Grid>
			) : null}
			{!!failedCaptures ? (
				<Grid container justifyContent="space-between">
					<Grid item>
						<Typography variant="caption" color="error">
							{t('common:payment.failedCaptures', 'Failed captures')}
						</Typography>
					</Grid>
					<Grid item>
						<Typography variant="caption" color="error">
							{getPricingString(failedCaptures, currencyObj)}
						</Typography>
					</Grid>
				</Grid>
			) : null}
			{!!originalPayment &&
				transactions.map((transaction) => (
					<React.Fragment key={transaction.id}>
						{!singleTransaction ? (
							<Divider
								className={classNames(
									classes.paymentDivider,
									!originalPayment && classes.darkDivider,
								)}
							/>
						) : null}
						<TransactionRow
							singleTransaction={singleTransaction}
							transaction={transaction}
							transactionLoading={transactionsLoading.includes(transaction.id)}
							modifyTransaction={modifyTransaction}
							shopCurrency={shopCurrency}
						/>
					</React.Fragment>
				))}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		paymentCard: {
			margin: theme.spacing(1, 0),
		},
		cardContent: {
			padding: 16,
			'&:last-child': {
				padding: 16,
			},
		},
		paymentDivider: {
			margin: theme.spacing(1, 0),
		},
		darkDivider: {
			backgroundColor: theme.palette.divider,
		},
		modificationPaymentWrapper: {
			margin: theme.spacing(1, -1),
			padding: theme.spacing(1),
			backgroundColor: theme.palette.background.secondary,
			borderRadius: theme.spacing(0.5),
		},
	}),
);

export default PaymentCard;
